import React from 'react';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';

import { Box, Chip, IconButton } from '@mui/material';
import AdminLayoutView from 'src/Layout/AdminLayout/AdminLayoutView';
import AdminTabs from 'src/Layout/AdminLayout/AdminTabs';
import { AdminTabValue } from 'src/Layout/AdminLayout/AdminTabs/types';
import AdminViewWrapper from 'src/Layout/AdminLayout/AdminViewWrapper';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { Button, Dropdown } from 'src/UIKit';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { useAdminPlansViewController } from '../../controllers';
import { CreateAdminPlanFormDrawer } from '../CreateAdminPlanForm';
import { CopySignupLinkButton } from './CopySignupLinkButton';

export const AdminPlansView = () => {
  const {
    isPageLoading,
    plansFilter,
    planList,
    archiveAdminPlan,
    unarchiveAdminPlan,
    changePlanFilterForm,
    changePlansSearchFilter,
    changePlansSorting,
    changePlansPagination,
    applicationOptions,
    createAdminPlanFormDrawerController,
  } = useAdminPlansViewController();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  return (
    <>
      <ConfirmationDialog />

      <CreateAdminPlanFormDrawer
        {...createAdminPlanFormDrawerController}
        applicationOptions={applicationOptions}
      />

      <AdminViewWrapper>
        <AdminLayoutView tabs={<AdminTabs value={AdminTabValue.plans} />}>
          <GridData
            header={
              <TableHeader
                searchValue={plansFilter.query}
                onSearchChange={changePlansSearchFilter}
                filters={
                  <>
                    <TableHeaderItem>
                      <Dropdown
                        label="Application"
                        value={plansFilter.applicationId}
                        options={applicationOptions}
                        onChange={(_, v) => changePlanFilterForm({ applicationId: v })}
                        margin="none"
                      />
                    </TableHeaderItem>

                    <TableHeaderItem>
                      <Dropdown
                        label="Types"
                        value={plansFilter.types}
                        options={[
                          { title: 'All', value: 'private,public' },
                          { title: 'Public', value: 'public' },
                          { title: 'Private', value: 'private' },
                        ]}
                        onChange={(_, v) => changePlanFilterForm({ types: v })}
                        margin="none"
                      />
                    </TableHeaderItem>

                    <TableHeaderItem>
                      <Dropdown
                        label="Statuses"
                        value={plansFilter.statuses}
                        options={[
                          { title: 'All', value: 'active,archived' },
                          { title: 'Active', value: 'active' },
                          { title: 'Archived', value: 'archived' },
                        ]}
                        onChange={(_, v) => changePlanFilterForm({ statuses: v })}
                        margin="none"
                      />
                    </TableHeaderItem>
                  </>
                }
                actions={
                  <Button
                    onClick={() => createAdminPlanFormDrawerController.open()}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    Create Plan
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={planList?.items}
            columns={[
              {
                field: 'name',
                headerName: 'Plan',
                renderCell: r => {
                  return (
                    <Box display="flex" alignItems="center" gap={0.5} height="100%">
                      {r.row.name}
                      {r.row.status === 'archived' && (
                        <Chip
                          color="default"
                          label="Archived"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  );
                },
                minWidth: 200,
                editable: false,
                flex: 1,
              },
              {
                field: 'applicationId',
                headerName: 'Application',
                renderCell: r => {
                  return applicationOptions.find(option => option.value === r.row.applicationId)
                    ?.title;
                },
                minWidth: 200,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: 'type',
                headerName: 'Type',
                renderCell: r => {
                  if (r.row.type === 'public') {
                    return (
                      <Chip label="Public" color="success" size="small" />
                    );
                  }

                  if (r.row.type === 'private') {
                    return (
                      <Chip
                        label="Private"
                        color="primary"
                        size="small"
                      />
                    );
                  }

                  return r.row.type;
                },
                minWidth: 100,
                sortable: false,
                editable: false,
                flex: 1,
              },
              {
                field: 'duration',
                headerName: 'Duration',
                renderCell: r => {
                  return (
                    <>
                      {r.row.duration} {r.row.period}
                    </>
                  );
                },
                minWidth: 100,
                sortable: false,
                editable: false,
                flex: 1,
              },
            ]}
            renderAction={r => {
              return (
                <>
                  <CopySignupLinkButton signupUrl={r.signupUrl} />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => createAdminPlanFormDrawerController.open(r)}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>

                  {r.status !== 'archived' && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        openDialog({
                          title: 'Archive Plan',
                          content: 'Are you sure you want to archive this Plan?',
                          onConfirm: () => archiveAdminPlan(r.id),
                        })
                      }
                    >
                      <ArchiveIcon fontSize="inherit" />
                    </IconButton>
                  )}
                  {r.status === 'archived' && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        openDialog({
                          title: 'Unarchive Plan',
                          content: 'Are you sure you want to unarchive this Plan?',
                          onConfirm: () => unarchiveAdminPlan(r.id),
                        })
                      }
                    >
                      <UnarchiveIcon fontSize="inherit" />
                    </IconButton>
                  )}
                </>
              );
            }}
            sorting={{
              value: plansFilter.sort,
              onChange: changePlansSorting,
            }}
            pagination={{
              page: plansFilter.page,
              pageSize: plansFilter.limit,
              totalCount: planList?.count,
              onChange: changePlansPagination,
            }}
          />
        </AdminLayoutView>
      </AdminViewWrapper>
    </>
  );
};
